import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Button,
  Box,
  Icon,
  Link,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { LuStar, LuBookmark, LuSparkles, LuUsers, LuStickyNote, LuRocket, LuUserPlus, LuLink, LuCopy } from 'react-icons/lu';
import { Sparkles } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

export type SignUpPromptContext =
  | 'rate'
  | 'save'
  | 'copy'
  | 'userCompatibility'
  | 'restaurantCompatibility'
  | 'checkins'
  | 'notes'
  | 'navigation'
  | 'connect'
  | 'connections';

interface SignUpPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: SignUpPromptContext;
}

const contextContent: Record<
  SignUpPromptContext,
  { icon: React.ElementType; title: string; description: string; color: string }
> = {
  rate: {
    icon: LuStar,
    title: 'Rate This Restaurant',
    description: 'Sign up to rate restaurants and get personalized matches based on your PersonalTaste™ Profile.',
    color: 'yellow.500',
  },
  save: {
    icon: LuBookmark,
    title: 'Save to Your Lists',
    description: 'Sign up to save restaurants to custom lists and never forget a great spot, or plan your next trip.',
    color: 'teal.500',
  },
  copy: {
    icon: LuCopy,
    title: 'Copy This List',
    description: 'Sign up to copy lists to your collection and customize them with your own favorites.',
    color: 'blue.500',
  },
  userCompatibility: {
    icon: LuSparkles,
    title: 'See Your TasteMatch™',
    description: 'Sign up to see your TasteMatch™ with this member and how your PersonalTaste™ Profiles align.',
    color: 'purple.500',
  },
  restaurantCompatibility: {
    icon: LuSparkles,
    title: 'See Your TasteMatch™',
    description: 'Sign up to see how well this restaurant matches your PersonalTaste™ Profile.',
    color: 'purple.500',
  },
  checkins: {
    icon: LuUsers,
    title: 'See Who Visited',
    description: 'Sign up to see when your friends and connections have been here.',
    color: 'blue.500',
  },
  notes: {
    icon: LuStickyNote,
    title: 'Keep Personal Notes',
    description: 'Sign up to save private notes about the restaurants you visit.',
    color: 'purple.500',
  },
  navigation: {
    icon: LuRocket,
    title: 'Unlock the Full Experience',
    description: 'Create a free account to explore restaurants, save your favorites and start building your PersonalTaste™ Profile.',
    color: 'purple.500',
  },
  connect: {
    icon: LuUserPlus,
    title: 'Connect With Friends',
    description: 'Sign up to add friends, follow food lovers and discover restaurants through your network.',
    color: 'blue.500',
  },
  connections: {
    icon: LuLink,
    title: 'See Their Network',
    description: 'Sign up to view connections, discover mutual friends and build your own food-loving community.',
    color: 'purple.500',
  },
};

export function SignUpPromptModal({
  isOpen,
  onClose,
  context = 'navigation',
}: SignUpPromptModalProps) {
  const navigate = useNavigate();
  const content = contextContent[context] ?? contextContent['navigation'];

  const handleCreateAccount = () => {
    // Track signup click from modal
    analytics.capture(AnalyticsEvents.SIGNUP_CLICKED, {
      source: 'signup_prompt_modal',
      context,
    });
    onClose();
    navigate({ to: '/login', search: { form: 'signup' } });
  };

  const handleLogin = () => {
    // Track login click from modal
    analytics.capture(AnalyticsEvents.LOGIN_CLICKED, {
      source: 'signup_prompt_modal',
      context,
    });
    onClose();
    navigate({ to: '/login', search: { form: 'login' } });
  };

  // Get color base for dynamic theming
  const colorBase = content.color.split('.')[0];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" mx={4} borderRadius="var(--r-card)" overflow="hidden">
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg={`${colorBase}.100`} borderRadius="var(--r-inner)" p={2}>
              <Icon as={content.icon} boxSize={5} color={content.color} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                {content.title}
              </Text>
              <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal">
                Join Paire to unlock this feature
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          <VStack spacing={4}>
            {/* Premium Feature Banner */}
            <Box
              bg={`linear-gradient(135deg, var(--chakra-colors-${colorBase}-500), var(--chakra-colors-purple-500))`}
              borderRadius="var(--r-card-compact)"
              p={4}
              position="relative"
              overflow="hidden"
              w="100%"
            >
              <Box position="absolute" top={-20} right={-20} opacity={0.2}>
                <Sparkles size={80} color="white" />
              </Box>
              <HStack spacing={3} position="relative" zIndex={1}>
                <Icon as={LuSparkles} boxSize={6} color="white" />
                <VStack align="start" spacing={0}>
                  <Text color="white" fontWeight="bold" fontSize="sm">
                    Free to Join
                  </Text>
                  <Text color="whiteAlpha.800" fontSize="xs">
                    Create your PersonalTaste™ Profile
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {/* Description */}
            <Text
              fontSize="sm"
              color="var(--card-text-2)"
              textAlign="center"
              lineHeight="tall"
            >
              {content.description}
            </Text>

            {/* Feature list */}
            <Box w="100%">
              <Text fontSize="xs" fontWeight="medium" color="var(--card-text-3)" mb={2}>
                WHAT YOU'LL GET:
              </Text>
              <VStack spacing={2} align="stretch">
                {[
                  'Personalized restaurant recommendations',
                  'Save favorites to custom lists',
                  'Connect with fellow food lovers',
                ].map((feature, i) => (
                  <HStack key={i} spacing={2}>
                    <Box w="4px" h="4px" borderRadius="full" bg={content.color} />
                    <Text fontSize="sm" color="var(--card-text-2)">{feature}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <VStack spacing={3} width="100%">
            <Button
              width="100%"
              size="lg"
              bg="purple.500"
              color="white"
              _hover={{ bg: 'purple.600' }}
              onClick={handleCreateAccount}
              leftIcon={<Icon as={LuUserPlus} />}
            >
              Create Account
            </Button>

            <Text fontSize="sm" color="var(--card-text-3)">
              Already have an account?{' '}
              <Link
                color="purple.500"
                fontWeight="medium"
                onClick={handleLogin}
                cursor="pointer"
              >
                Log in
              </Link>
            </Text>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
